import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui';
import { useLearning } from '../../context/LearningContext';
import { useToast } from '../../context/ToastContext';
import { isAiGeneratedCard } from './aiGenerated.js';
import './DeckManager.css';

// Re-exported for backward compatibility — CardRenderer.jsx (the card a
// learner actually studies) now shares the same helper; see aiGenerated.js
// for the full Art. 50 Abs. 2 rationale.
export { isAiGeneratedCard };

export default function DeckManager() {
  const { t } = useTranslation();
  const { decks, fetchDecks, generateCards, createCard } = useLearning();
  const toast = useToast();
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState({ bloom: '', search: '' });
  const [generating, setGenerating] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [newCardError, setNewCardError] = useState(null);

  useEffect(() => { fetchDecks(); }, [fetchDecks]);

  useEffect(() => {
    if (selectedDeck) loadCards(selectedDeck.id);
  }, [selectedDeck, filter]);

  async function loadCards(deckId) {
    // The workspace is the server-side scope for card reads — a deck is only
    // readable from the workspace it belongs to.
    const workspace = decks.find((deck) => deck.id === deckId)?.workspace_path;
    if (!workspace) return;

    const params = new URLSearchParams({ userId: 'local', workspace });
    if (filter.bloom) params.set('bloom', filter.bloom);
    if (filter.search) params.set('search', filter.search);

    const res = await fetch(`/api/learning/decks/${deckId}/cards?${params}`);
    if (res.ok) {
      const data = await res.json();
      setCards(data.cards);
    }
  }

  async function handleGenerate() {
    if (!selectedDeck) return;
    setGenerating(true);
    try {
      await generateCards(selectedDeck.id, 'module', selectedDeck.source_ref);
      await loadCards(selectedDeck.id);
    } catch (err) {
      // generateCards() doesn't go through apiClient (bare fetch in
      // LearningContext.jsx), so a used-up budget arrives as
      // `new Error('quota_exceeded')` with no `.status` attached. Show the
      // same friendly explanation the chat surface shows instead of the raw
      // code.
      if (err.message === 'quota_exceeded') {
        toast.error(t('states.quotaExceededHint'));
      } else {
        toast.error(err.message || 'Failed to generate cards');
      }
    } finally {
      setGenerating(false);
    }
  }

  async function handleSaveEdit(cardId, front, back) {
    const res = await fetch(`/api/learning/cards/${cardId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ front, back }),
    });
    if (res.ok) {
      setEditingCard(null);
      await loadCards(selectedDeck.id);
    }
  }

  async function handleDeleteCard(cardId) {
    const res = await fetch(`/api/learning/cards/${cardId}`, { method: 'DELETE' });
    if (res.ok) {
      setCards(prev => prev.filter(c => c.id !== cardId));
    }
  }

  async function handleCreateCard(front, back, bloomLevel) {
    if (!selectedDeck) return;
    setNewCardError(null);
    try {
      await createCard(selectedDeck.id, front, back, { bloom_level: bloomLevel });
      await loadCards(selectedDeck.id);
      setShowNewCard(false);
    } catch (err) {
      setNewCardError(err.message);
    }
  }

  return (
    <div className="deck-manager">
      <div className="deck-sidebar">
        <h2>{t('learn.decks')}</h2>
        {decks.map(deck => (
          <button
            key={deck.id}
            className={`deck-tab ${selectedDeck?.id === deck.id ? 'active' : ''}`}
            onClick={() => setSelectedDeck(deck)}
          >
            {deck.title}
          </button>
        ))}
      </div>

      <div className="deck-content">
        {selectedDeck ? (
          <>
            <div className="deck-toolbar">
              <h2>{selectedDeck.title}</h2>
              <div className="deck-filters">
                <select value={filter.bloom} onChange={e => setFilter(prev => ({ ...prev, bloom: e.target.value }))}>
                  <option value="">{t('learn.allLevels')}</option>
                  {['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create'].map(b => (
                    <option key={b} value={b}>{t(`learn.bloomLevels.${b}`)}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder={t('learn.searchCardsPlaceholder')}
                  value={filter.search}
                  onChange={e => setFilter(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              <div className="deck-toolbar-actions">
                <button className="btn-secondary" onClick={() => { setShowNewCard(true); setNewCardError(null); }}>
                  {t('learn.newCard')}
                </button>
                <button className="btn-primary" onClick={handleGenerate} disabled={generating}>
                  {generating ? t('learn.generating') : t('learn.generateCards')}
                </button>
              </div>
            </div>

            {showNewCard && (
              <NewCardForm
                t={t}
                error={newCardError}
                onSave={handleCreateCard}
                onCancel={() => { setShowNewCard(false); setNewCardError(null); }}
              />
            )}

            <div className="card-table">
              {cards.map(card => (
                <div
                  key={card.id}
                  className={`card-row ${card.lapses >= 8 ? 'leech' : ''}`}
                  data-ai-generated={isAiGeneratedCard(card) || undefined}
                >
                  {editingCard === card.id ? (
                    <EditCardForm
                      card={card}
                      onSave={(front, back) => handleSaveEdit(card.id, front, back)}
                      onCancel={() => setEditingCard(null)}
                    />
                  ) : (
                    <>
                      <div className="card-preview">
                        <span className="card-front-text">{card.front}</span>
                        <span className={`bloom-badge bloom-${card.bloom_level}`}>{card.bloom_level}</span>
                        {card.lapses >= 8 && <span className="leech-badge">{t('learn.leech')}</span>}
                        {isAiGeneratedCard(card) && (
                          <Badge tone="brand" title={t('learn.aiBadgeLabel')}>
                            <span aria-hidden="true">{t('learn.aiBadge')}</span>
                            <span className="etch-visually-hidden">{t('learn.aiBadgeLabel')}</span>
                          </Badge>
                        )}
                      </div>
                      <div className="card-actions">
                        <button onClick={() => setEditingCard(card.id)}>{t('learn.edit')}</button>
                        <button className="btn-danger" onClick={() => handleDeleteCard(card.id)}>{t('learn.delete')}</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="empty-state">{t('learn.selectDeckHint')}</p>
        )}
      </div>
    </div>
  );
}

function EditCardForm({ card, onSave, onCancel }) {
  const { t } = useTranslation();
  const [front, setFront] = useState(card.front);
  const [back, setBack] = useState(card.back);

  return (
    <div className="edit-card-form">
      <textarea value={front} onChange={e => setFront(e.target.value)} placeholder={t('learn.cardFront')} />
      <textarea value={back} onChange={e => setBack(e.target.value)} placeholder={t('learn.cardBack')} />
      <div className="edit-actions">
        <button className="btn-primary" onClick={() => onSave(front, back)}>{t('learn.save')}</button>
        <button onClick={onCancel}>{t('learn.cancel')}</button>
      </div>
    </div>
  );
}

function NewCardForm({ t, error, onSave, onCancel }) {
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [bloomLevel, setBloomLevel] = useState('remember');
  const canSave = front.trim().length > 0 && back.trim().length > 0;

  return (
    <div className="edit-card-form new-card-form">
      <div className="new-card-header">{t('learn.newCardTitle')}</div>
      <textarea
        value={front}
        onChange={e => setFront(e.target.value)}
        placeholder={t('learn.cardFront')}
        autoFocus
      />
      <textarea
        value={back}
        onChange={e => setBack(e.target.value)}
        placeholder={t('learn.cardBack')}
      />
      <select value={bloomLevel} onChange={e => setBloomLevel(e.target.value)}>
        {['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create'].map(b => (
          <option key={b} value={b}>{t(`learn.bloomLevels.${b}`)}</option>
        ))}
      </select>
      {error && <div className="new-card-error">{error}</div>}
      <div className="edit-actions">
        <button
          className="btn-primary"
          disabled={!canSave}
          onClick={() => onSave(front.trim(), back.trim(), bloomLevel)}
        >
          {t('learn.save')}
        </button>
        <button onClick={onCancel}>{t('learn.cancel')}</button>
      </div>
    </div>
  );
}
